import { useEffect, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { adminDivisionsApi, adminGradesApi, adminBoardsApi } from '@/api/admin';
import { getUserData } from '@/utils/auth';
import { trackEvent, attachUserProps } from '@/analytics/analytics';

const selectStyle = { backgroundColor: 'hsl(var(--input-bg))', color: 'hsl(var(--foreground))' };

export default function AdminClasses() {
  const [divisions, setDivisions] = useState([]);
  const [gradesConfigured, setGradesConfigured] = useState([]);
  const [gradesAll, setGradesAll] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addGradeOpen, setAddGradeOpen] = useState(false);
  const [addGradeForm, setAddGradeForm] = useState({ grade: '', board: '', division_name: 'A' });
  const [savingAddGrade, setSavingAddGrade] = useState(false);
  const [newDivisionNames, setNewDivisionNames] = useState({});
  const [divisionToDelete, setDivisionToDelete] = useState(null);
  const [deletingDivision, setDeletingDivision] = useState(false);
  const [gradeToRemove, setGradeToRemove] = useState(null);
  const [removingGrade, setRemovingGrade] = useState(false);
  const schoolId = useMemo(() => getUserData('admin')?.school_id, []);

  const loadMeta = async () => {
    try {
      const [gradesConfiguredRes, gradesAllRes, boardsRes] = await Promise.all([
        adminGradesApi.list(),
        adminGradesApi.listAll(),
        adminBoardsApi.listAll(),
      ]);
      setGradesConfigured(gradesConfiguredRes.data?.results || gradesConfiguredRes.data || []);
      setGradesAll(gradesAllRes.data?.results || gradesAllRes.data || []);
      setBoards(boardsRes.data?.results || boardsRes.data || []);
    } catch (err) {
      console.error('Failed to load grades or boards', err);
      setError('Unable to load grades/boards.');
    }
  };

  const loadDivisions = async () => {
    if (!schoolId) {
      setError('School not found for this admin account.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await adminDivisionsApi.list();
      setDivisions(res.data.results || res.data || []);
    } catch (err) {
      console.error('Failed to load divisions', err);
      setError('Unable to load classes/divisions right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    loadDivisions();
  }, []);

  const boardForGrade = (gradeId) => {
    const configured = gradesConfigured.find((g) => String(g.id) === String(gradeId));
    if (configured?.board_id_for_school) return configured.board_id_for_school;
    const divisionBoard = divisions.find((d) => String(d.grade) === String(gradeId))?.board_id;
    return divisionBoard || '';
  };

  const gradeCards = useMemo(() => {
    return gradesConfigured.map((grade) => {
      const divs = divisions.filter((d) => String(d.grade) === String(grade.id));
      const boardId = boardForGrade(grade.id);
      const boardName = boards.find((b) => String(b.id) === String(boardId))?.board_name || grade.board_name_for_school || divs[0]?.board_name;
      return {
        ...grade,
        boardId,
        boardName,
        divisions: divs,
      };
    });
  }, [gradesConfigured, divisions, boards]);

  const handleAddGrade = async () => {
    if (!addGradeForm.grade || !addGradeForm.board || !addGradeForm.division_name) return;
    setSavingAddGrade(true);
    try {
      await adminDivisionsApi.create({
        grade: addGradeForm.grade,
        division_name: addGradeForm.division_name,
        board: addGradeForm.board,
      });
      try { trackEvent('admin_grade_created', attachUserProps({ grade: addGradeForm.grade, board: addGradeForm.board, division_name: addGradeForm.division_name, page: 'admin_classes' })); } catch (e) {}
      setAddGradeOpen(false);
      setAddGradeForm({ grade: '', board: '', division_name: 'A' });
      await Promise.all([loadDivisions(), loadMeta()]);
    } catch (err) {
      console.error('Failed to add grade/division', err);
      setError(err.response?.data?.board?.[0] || err.response?.data?.grade?.[0] || err.response?.data?.detail || 'Could not create grade.');
    } finally {
      setSavingAddGrade(false);
    }
  };

  const handleAddDivision = async (gradeId) => {
    const name = newDivisionNames[gradeId]?.trim();
    if (!name) return;
    try {
      await adminDivisionsApi.create({
        grade: gradeId,
        division_name: name,
        board: boardForGrade(gradeId) || undefined,
      });
      setNewDivisionNames((prev) => ({ ...prev, [gradeId]: '' }));
      await loadDivisions();
      try { trackEvent('admin_division_created', attachUserProps({ grade_id: gradeId, division_name: name, page: 'admin_classes' })); } catch (e) {}
    } catch (err) {
      console.error('Failed to create division', err);
      setError(err.response?.data?.detail || err.response?.data?.division_name?.[0] || 'Could not create division.');
    }
  };

  const handleDeleteDivision = async (divisionId) => {
    try {
      await adminDivisionsApi.delete(divisionId);
      await loadDivisions();
      try { trackEvent('admin_division_deleted', attachUserProps({ division_id: divisionId, page: 'admin_classes' })); } catch (e) {}
    } catch (err) {
      console.error('Failed to delete division', err);
      setError('Could not delete division.');
      throw err;
    }
  };

  const handleRemoveGrade = async (gradeId) => {
    try {
      await adminGradesApi.remove(gradeId);
      await Promise.all([loadDivisions(), loadMeta()]);
      try { trackEvent('admin_grade_removed', attachUserProps({ grade_id: gradeId, page: 'admin_classes' })); } catch (e) {}
    } catch (err) {
      console.error('Failed to remove grade', err);
      setError(err.response?.data?.detail || 'Could not remove grade.');
      throw err;
    }
  };

  const confirmDeleteDivision = async () => {
    if (!divisionToDelete) return;
    setDeletingDivision(true);
    try {
      await handleDeleteDivision(divisionToDelete.id);
      setDivisionToDelete(null);
    } catch (err) {
      // handled above
    } finally {
      setDeletingDivision(false);
    }
  };

  const confirmRemoveGrade = async () => {
    if (!gradeToRemove) return;
    setRemovingGrade(true);
    try {
      await handleRemoveGrade(gradeToRemove.id);
      setGradeToRemove(null);
    } catch (err) {
      // handled above
    } finally {
      setRemovingGrade(false);
    }
  };

  const availableGradesForAdd = useMemo(() => {
    const configuredIds = new Set(gradesConfigured.map((g) => String(g.id)));
    return gradesAll.filter((g) => !configuredIds.has(String(g.id)));
  }, [gradesAll, gradesConfigured]);

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] p-6 overflow-y-auto">
      <div className="max-w-7xl mx-auto w-full space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Class Configuration</h1>
            <p className="text-muted-foreground text-sm">Add grades with boards, then add divisions under each grade.</p>
          </div>
          <Button onClick={() => { setAddGradeOpen(true); try { trackEvent('admin_add_grade_open', attachUserProps({ page: 'admin_classes' })); } catch (e) {} }} size="sm" className="gap-2">
            <Plus className="h-4 w-4" /> Add Grade
          </Button>
          
        </div>

        {error && <div className="text-sm text-red-400">{error}</div>}

        {addGradeOpen && (
          <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-foreground text-lg">Add Grade</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setAddGradeOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">Grade</Label>
                <select
                  style={selectStyle}
                  className="w-full rounded-md border bg-[hsl(var(--input-bg))] text-foreground p-2 text-sm"
                  value={addGradeForm.grade}
                  onChange={(e) => setAddGradeForm((p) => ({ ...p, grade: e.target.value }))}
                >
                  <option value="">Select grade</option>
                  {availableGradesForAdd.map((g) => (
                    <option key={g.id} value={g.id}>{g.grade_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Curriculum / Board</Label>
                <select
                  style={selectStyle}
                  className="w-full rounded-md border bg-[hsl(var(--input-bg))] text-foreground p-2 text-sm"
                  value={addGradeForm.board}
                  onChange={(e) => setAddGradeForm((p) => ({ ...p, board: e.target.value }))}
                >
                  <option value="">Select board</option>
                  {boards.map((b) => (
                    <option key={b.id} value={b.id}>{b.board_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">First Division</Label>
                <Input
                  value={addGradeForm.division_name}
                  onChange={(e) => setAddGradeForm((p) => ({ ...p, division_name: e.target.value }))}
                  placeholder="e.g., A"
                  className="bg-[hsl(var(--input-bg))] text-foreground"
                />
              </div>
              <div className="md:col-span-3 flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setAddGradeOpen(false)}>Cancel</Button>
                <Button onClick={handleAddGrade} disabled={savingAddGrade || !addGradeForm.grade || !addGradeForm.board || !addGradeForm.division_name}>
                  {savingAddGrade ? 'Creating…' : 'Create grade'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="text-muted-foreground text-sm">Loading…</div>
        ) : gradeCards.length === 0 ? (
          <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
            <CardContent className="p-6 text-muted-foreground text-sm">No grades configured yet. Add a grade to get started.</CardContent>
          </Card>
        ) : (
          gradeCards.map((grade) => (
            <Card key={grade.id} className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 flex items-center justify-center rounded-lg bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] text-lg font-bold text-foreground">
                    {grade.grade_number || grade.grade_name}
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{grade.grade_name}</p>
                        <p className="text-xs text-muted-foreground">Configure board and divisions for this grade.</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-muted-foreground">Board</Label>
                        <select
                          style={selectStyle}
                          className="rounded-md border bg-[hsl(var(--input-bg))] text-foreground p-2 text-sm"
                          value={grade.boardId || ''}
                          disabled
                        >
                          <option value="">Select board</option>
                          {boards.map((b) => (
                            <option key={b.id} value={b.id}>{b.board_name}</option>
                          ))}
                        </select>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setGradeToRemove({ id: grade.id, name: grade.grade_name, board: grade.boardName })}
                          aria-label="Remove grade"
                        >
                          <X className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-foreground">Divisions:</p>
                      <div className="flex flex-wrap gap-2">
                        {grade.divisions.map((d) => (
                          <span key={d.id} className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--card-border))] bg-[hsl(var(--main-bg))] px-3 py-1 text-sm text-foreground">
                            {d.division_name}
                            <button
                              type="button"
                              onClick={() => setDivisionToDelete({ id: d.id, name: d.division_name, grade: grade.grade_name })}
                              className="text-muted-foreground hover:text-foreground"
                              aria-label="Remove division"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                        {grade.divisions.length === 0 && (
                          <span className="text-xs text-muted-foreground">No divisions yet.</span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                      <Input
                        placeholder="Enter division"
                        value={newDivisionNames[grade.id] || ''}
                        onChange={(e) => setNewDivisionNames((prev) => ({ ...prev, [grade.id]: e.target.value }))}
                        className="bg-[hsl(var(--input-bg))] text-foreground sm:w-64"
                      />
                      <Button size="sm" onClick={() => handleAddDivision(grade.id)} className="gap-2">
                        <Plus className="h-4 w-4" /> Add Division
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}

      </div>

      <AlertDialog
        open={Boolean(divisionToDelete)}
        onOpenChange={(open) => {
          if (!open && !deletingDivision) setDivisionToDelete(null);
        }}
      >
        <AlertDialogContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground text-lg">Delete division?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground text-sm space-y-1">
              <p>Division: {divisionToDelete?.name || '—'}</p>
              <p>Grade: {divisionToDelete?.grade || '—'}</p>
              <p>This will remove the division for all teachers and students assigned to it.</p>
              <p>Do you want to continue?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDivisionToDelete(null)} disabled={deletingDivision}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteDivision} disabled={deletingDivision}>
              {deletingDivision ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(gradeToRemove)}
        onOpenChange={(open) => {
          if (!open && !removingGrade) setGradeToRemove(null);
        }}
      >
        <AlertDialogContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground text-lg">Remove grade?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground text-sm space-y-1">
              <p>Grade: {gradeToRemove?.name || '—'}</p>
              <p>Board: {gradeToRemove?.board || '—'}</p>
              <p>This will remove the grade, its divisions, and related assignments for your school.</p>
              <p>Do you want to proceed?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setGradeToRemove(null)} disabled={removingGrade}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveGrade} disabled={removingGrade}>
              {removingGrade ? 'Removing…' : 'Remove grade'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
